import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';

const PlantsPage = () => {
  const navigate = useNavigate();
  const [genera, setGenera] = useState([]);
  const [genusPhotoMap, setGenusPhotoMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState('popular');
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const fetchGenusPhotos = async () => {
      const { data } = await supabase.from('genus_photos').select('*');
      if (data) {
        const map = {};
        data.forEach(p => { map[p.genus.toLowerCase()] = p.image_url; });
        setGenusPhotoMap(map);
      }
    };
    fetchGenusPhotos();

    const fetch = async () => {
      const { data } = await supabase.from('plants').select('common_name, genus, sold_price, image_url').eq('status', 'approved');
      if (data) {
        // Group by genus
        const genusMap = {};
        data.forEach(p => {
          const genus = p.genus?.trim() || 'Other';
          if (!genusMap[genus]) genusMap[genus] = { name: genus, count: 0, plants: [], image: null, topPrice: 0 };
          genusMap[genus].count++;
          if (!genusMap[genus].image && p.image_url) genusMap[genus].image = p.image_url;
          const price = parseFloat(String(p.sold_price || '').replace(/[^0-9.]/g, ''));
          if (price > genusMap[genus].topPrice) genusMap[genus].topPrice = price;
          if (!genusMap[genus].plants.includes(p.common_name)) genusMap[genus].plants.push(p.common_name);
        });
        const sorted = Object.values(genusMap).sort((a, b) => b.count - a.count);
        setGenera(sorted);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const filtered = genera
    .filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => sortMode === 'az' ? a.name.localeCompare(b.name) : b.count - a.count);

  useEffect(() => {
    document.title = 'Plant Database - Browse by Genus | True Plant Value';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Browse rare and common houseplants by genus with real sold price data from collectors. Monstera, Alocasia, Philodendron, and more.');
    return () => { document.title = 'True Plant Value'; };
  }, []);

  return (
    <div style={{ paddingBottom: '80px' }}>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #1e3d20 0%, #2c4a2e 60%, #4a7c59 100%)', padding: 'clamp(48px, 8vw, 80px) 24px', textAlign: 'center', borderRadius: '0 0 40px 40px', marginBottom: '48px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '6px', color: '#8fb99a', textTransform: 'uppercase', marginBottom: '16px', fontFamily: 'Lato, sans-serif' }}>🌱 Plant Database</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '36px' : '56px', color: '#f5f0e8', fontWeight: 700, marginBottom: '16px', lineHeight: 1.1 }}>Browse by Plant Family</h1>
        <p style={{ color: 'rgba(245,240,232,0.8)', fontFamily: 'Lato, sans-serif', fontSize: '18px', maxWidth: '500px', margin: '0 auto 32px', lineHeight: 1.7 }}>
          Explore {genera.length} genera with real sold price data from collectors across the US.
        </p>
        {/* Search */}
        <div style={{ maxWidth: '440px', margin: '0 auto' }}>
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search genus... e.g. Monstera, Alocasia"
            style={{ width: '100%', padding: '16px 24px', border: 'none', borderRadius: '14px', fontSize: '16px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', outline: 'none', boxSizing: 'border-box', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          />
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontStyle: 'italic' }}>Loading plant families...</p>
        ) : (
          <>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '0' }}>
              Showing <strong>{filtered.length}</strong> genera
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', marginTop: '12px' }}>
              <button onClick={() => setSortMode('popular')} style={{ padding: '6px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif', background: sortMode === 'popular' ? 'linear-gradient(135deg, #2c4a2e, #4a7c59)' : '#f0f7f1', color: sortMode === 'popular' ? '#f5f0e8' : '#4a7c59' }}>🔥 Popular</button>
              <button onClick={() => setSortMode('az')} style={{ padding: '6px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif', background: sortMode === 'az' ? 'linear-gradient(135deg, #2c4a2e, #4a7c59)' : '#f0f7f1', color: sortMode === 'az' ? '#f5f0e8' : '#4a7c59' }}>A-Z</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' }}>
              {filtered.map((genus) => (
                <div key={genus.name} onClick={() => navigate(`/search?q=${encodeURIComponent(genus.name)}`)}
                  style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(44,74,46,0.15)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(44,74,46,0.08)'; }}
                >
                  {(genusPhotoMap[genus.name.toLowerCase()] || genus.image)
                    ? <img src={genusPhotoMap[genus.name.toLowerCase()] || genus.image} alt={genus.name} style={{ width: '100%', height: '140px', objectFit: 'cover' }} />
                    : <div style={{ height: '140px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px' }}>🌿</div>
                  }
                  <div style={{ padding: '14px 16px' }}>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20', margin: '0 0 6px' }}>{genus.name}</h3>
                    <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a5e4b', marginBottom: '4px' }}>
                      {[...new Set(genus.plants)].length} varieties · {genus.count} sales
                    </div>
                    {genus.topPrice > 0 && (
                      <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a7c59', fontWeight: 700 }}>
                        Up to ${genus.topPrice.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlantsPage;